import { LitElement, html, css, ref, createRef, unsafeHTML } from 'lit-all'
import styles from 'lib-styles'
import { t, updateCartIconBadge } from 'lib-utils'

class NovaMiniCart extends LitElement {
  static properties = {
    initialItems: { type: Array },
    initialData: { type: Object },
    items: { type: Array, state: true },
    donationBanner: { type: String, state: true },
    emptyCartText: { type: String, attribute: 'empty-cart-text' },
    emptyCartButton: { type: String, attribute: 'empty-cart-button' },
    aboutCheckout: { type: String, attribute: 'about-checkout' },
    showFreeShipping: { type: Boolean },
    freeShippingThreshold: { type: Number },
    productRecommendationsTitle: {
      type: String,
      attribute: 'product-recommendations-title',
    },
    reachLimitInventoryText: {
      type: String,
      attribute: 'reach-limit-inventory-text',
    },
    storeCountry: {
      type: String,
      attribute: 'store-country',
    },
    cartDataFromTemplete: { type: Object },
    previousActiveElement: { type: Object },
  }

  constructor() {
    super()
    document.addEventListener('nova:cart:open', this._handleOpenCart)
    document.addEventListener('nova:cart:close', this._handleCloseCart)
    document.addEventListener('nova:cart:refresh', this.refresh)
    this.initialItems = []
    this.items = []
    this.initialData = {}
    this.cartDataFromTemplete = {}
    this.$cartDrawer = createRef()
  }

  connectedCallback() {
    super.connectedCallback()
    // Get storeCountry from localization if not provided
    if (!this.storeCountry) {
      this.storeCountry = window['Localization'] && window['Localization'].storeCountry
        ? window['Localization'].storeCountry
        : 'United States';
    }
    this.queryCartDataFromTemplate().then((data) => {
      this.cartDataFromTemplete = data
    })
  }

  disconnectedCallback() {
    document.removeEventListener('nova:cart:open', this._handleOpenCart)
    document.removeEventListener('nova:cart:close', this._handleCloseCart)
    document.removeEventListener('nova:cart:refresh', this.refresh)
  }

  get subtotal() {
    return this.items.reduce(
      (acc, current) =>
        acc + Number.parseFloat(current.final_price) * current.quantity,
      0
    )
  }

  async fetchShippingRates(postcode, country, state) {
    try {
      const response = await fetch(
        `/cart/shipping_rates.json?shipping_address[zip]=${postcode}&shipping_address[country]=${country}&shipping_address[province]=${state}`
      );
      return await response.json();
    } catch (error) {
      console.error('Error fetching shipping rates:', error);
      throw error;
    }
  }

  async fetchCartItems() {
    try {
      const response = await fetch('/cart.json');
      const cartData = await response.json();
      return cartData.items;
    } catch (error) {
      console.error('Error fetching cart items:', error);
      throw error;
    }
  }

  filterApplicableShippingRates(cartItems, shippingRates) {
    return shippingRates.filter(rate => {
      const maxWeight = rate.maximum_weight || Infinity;
      return cartItems.every(item => item.grams <= maxWeight);
    });
  }

  async estimateShipping() {
    try {
      let postcode, country, state;

      switch (this.storeCountry) {
        case 'United States':
          postcode = '10001';
          country = 'US';
          state = 'NY';
          break;
        case 'Germany':
          postcode = '10115';
          country = 'DE';
          state = 'Berlin';
          break;
        case 'Netherlands':
          postcode = '1012';
          country = 'NL';
          state = 'North Holland';
          break;
        case 'France':
          postcode = '75001';
          country = 'FR';
          state = 'Île-de-France';
          break;
        case 'Belgium':
          postcode = '1000';
          country = 'BE';
          state = 'Brussels-Capital';
          break;
        case 'United Kingdom':
          postcode = 'SW1A 1AA';
          country = 'GB';
          state = 'England';
          break;
        default:
          postcode = '10001';
          country = 'US';
          state = 'NY';
      }

      const [cartItems, shippingRates] = await Promise.all([
        this.fetchCartItems(),
        // this.fetchShippingRates(postcode, country, state),
      ]);

      // return this.filterApplicableShippingRates(cartItems, shippingRates.shipping_rates);;
    } catch (error) {
      console.error('Error estimating shipping:', error);
      return null;
    }
  }


  formatMoney(cents) {
    if (cents === '' || cents === null || cents === undefined) {
      return '';
    }

    // If cents is a string, ensure no unwanted characters are present.
    if (typeof cents === 'string') {
      cents = cents.replace(',', '');
    }

    const value = parseFloat(cents || 0);

    // Ensure two decimal places are always shown
    const formattedValue = value.toFixed(2);

    // Use proper currency formatting based on the locale
    const locale = 'de-DE';
    const formattedAmount = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);

    return formattedAmount;
  }


  get formattedSubtotal() {
    return this.formatMoney(this.subtotal)
  }

  get totalDiscount() {
    return this.items.reduce((acc, item) => {
      const comparePrice = this.handleCompareAtPrice(item)
      if (comparePrice) {
        const cmp = parseInt(comparePrice, 10)
        const final = parseInt(item.final_price, 10)
        if (cmp > final) return acc + ((cmp - final) * item.quantity)
      }
      return acc
    }, 0)
  }

  get formattedTotalDiscount() {
    return '-' + this.formatMoney(this.totalDiscount)
  }

  get itemCount() {
    return this.items
      ? this.items.reduce(
        (total, item) => total + Number.parseInt(item.quantity),
        0
      )
      : 0
  }

  async queryCartDataFromTemplate() {
    try {
      const response = await fetch('/cart?view=data')
      const data = await response.text()
      const parser = new DOMParser()
      const htmlDocument = parser.parseFromString(data, 'text/html')
      const targetElement = htmlDocument.querySelector('.js-cart-data')
      if (!targetElement) return null
      return JSON.parse(targetElement.textContent)
    } catch (error) {
      console.error('Error fetching cart data:', error)
      return null
    }
  }

  handleCompareAtPrice(currentItem) {
    return this.initialItems.find((item) => item.key === currentItem.key)
      ?.compare_at_price
  }

  _handleOpenCart = () => {
    const externalDrawer = document.querySelector('sl-drawer.cart-drawer');
    if (externalDrawer) {
      externalDrawer.show();
      externalDrawer.focus();
    } else {
      console.warn('Cart drawer not found in DOM.');
    }
  };

  _handleCloseCart = () => {
    this.$cartDrawer.value.hide()
  }

  updateCartDataFromTemplateToItems(items) {
    if (!this.cartDataFromTemplete || !this.cartDataFromTemplete.cart_items) {
      return
    }

    for (const item of items) {
      const cartItem = this.cartDataFromTemplete.cart_items.find(
        (cart_item) => Number(cart_item.id) === Number(item.id)
      )
      if (cartItem && cartItem.inventory_policy !== 'continue') {
        item.inventory_quantity = cartItem.inventory_quantity
      }
    }
  }

  refresh = async () => {

    const cartDrawer = document.querySelector('sl-drawer.cart-drawer');
    cartDrawer.classList.add('cart-loading');

    try {
      // Fetch updated cart data as JSON
      const cartResponse = await fetch('/cart.json');
      const cartData = await cartResponse.json();
      this.items = cartData.items;

      // Update inventory quantities from the Liquid template data, if available
      if (Object.keys(this.cartDataFromTemplete).length !== 0) {
        this.updateCartDataFromTemplateToItems(this.items);
      }

      // Fetch updated HTML for the cart drawer using Shopify's section rendering API
      const sectionResponse = await fetch('/cart?section_id=cart-drawer');
      const html = await sectionResponse.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Locate the new and existing sl-drawer elements
      const newDrawer = doc.querySelector('#CartDrawer sl-drawer');
      const oldDrawer = document.querySelector('#CartDrawer sl-drawer');

      if (!newDrawer || !oldDrawer) {
        console.warn('Cart drawer element not found');
        return;
      }

      oldDrawer.innerHTML = newDrawer.innerHTML

      updateCartIconBadge()

      cartDrawer.classList.remove('cart-loading');
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initialItems')) {
      this.items = this.initialItems
    }

    if (changedProperties.has('items')) {
      this.queryCartDataFromTemplate().then((data) => {
        this.cartDataFromTemplete = data
        this.handleGlobalCartData()
      })
    }
  }

  handleGlobalCartData() {
    window.cartData = this.items.map((item) => ({
      variant_id: item.variant_id || item.id,
      quantity: item.quantity,
    }))

    document.dispatchEvent(
      new CustomEvent('nova:cartData:updated', {
        bubbles: true,
      })
    )
  }

  // Not rendering anything here because it is causing issues
  render() {
    return html`
    `
  }

  static styles = [
    styles.a11y,
    styles.flex,
    styles.typography,
    css`
      /* Your existing CSS, commented or not, remains here */
    `,
  ]
}

customElements.define('nova-mini-cart', NovaMiniCart)

// This event handler handles the quantity updates
document.addEventListener('changeQuantityStarted', async (event) => {
  const quantity = event.detail.value;
  const key = event.detail.key;

  // Ensure both key and quantity are valid before proceeding
  if (!key || isNaN(quantity)) return;

  try {
    // Send quantity update request to Shopify's cart API
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: key, quantity }),
    });

    // Trigger a refresh event to allow the mini cart to update itself
    // This should update both the DOM (cart drawer content) and badge icon
    document.dispatchEvent(new CustomEvent('nova:cart:refresh'));
  } catch (err) {
    // Handle any errors that occur during the update
    console.error('Failed to update cart quantity:', err)
  }

  // If we are on the cart page, we need to update the content
  const cartPage = document.querySelector('#CartPage')
  if (cartPage) {
    document.dispatchEvent(new CustomEvent('nova:cartPage:refresh'))
  }
});

// Updated remove item handler to prevent redirection and refresh the cart after removal.
document.addEventListener('click', (event) => {
  const removeButton = event.target.closest('.cart-item__remove');
  if (!removeButton) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const itemKey = removeButton.dataset.key;
  if (!itemKey) {
    console.warn('Remove button clicked without valid data-key');
    return;
  }

  fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ id: itemKey, quantity: 0 }),
  })
    .then((response) =>
      response.ok ? response.json() : Promise.reject(response)
    )
    .then((data) => {
      document.dispatchEvent(new CustomEvent('nova:cart:refresh'))

      const cartPage = document.querySelector('#CartPage')
      if (cartPage) {
        document.dispatchEvent(new CustomEvent('nova:cartPage:refresh'))
      }
    })
    .catch(error => console.error('Failed to remove item:', error));
});

// Cart opening and closing
document.querySelectorAll('.js-cart-toggle').forEach((el) =>
  el.addEventListener('click', () => {
    document.dispatchEvent(new CustomEvent('nova:cart:open'));
  })
);